import type { BoardData } from '../model/schema.js';
import { RULES } from './registry.js';
import type { Finding, Severity, ValidateOptions } from './types.js';

const SEVERITY_ORDER: Record<Severity, number> = { error: 0, warning: 1, hint: 2 };

/** Prop key that suppresses `ruleId` on an item; its value is the reason. */
export function suppressKey(ruleId: string): string {
  return `suppress:${ruleId}`;
}

/** Suppression reason for a finding: the first involved item carrying `suppress:<ruleId>`. */
export function suppressionReason(data: BoardData, finding: Finding): string | undefined {
  for (const id of finding.entityIds) {
    const value = data.items[id]?.props[suppressKey(finding.ruleId)];
    if (value === undefined || value === false || value === '') continue;
    return String(value);
  }
  return undefined;
}

/** Orders findings by severity, rule id, then entity ids. */
export function compareFindings(a: Finding, b: Finding): number {
  return (
    SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity] ||
    a.ruleId.localeCompare(b.ruleId) ||
    a.entityIds.join(',').localeCompare(b.entityIds.join(','))
  );
}

/**
 * Runs the validation rules over a board. Rules only use formal data that exists; suppressed
 * findings are dropped unless `includeSuppressed` is set. The result is sorted.
 */
export function validate(data: BoardData, opts: ValidateOptions = {}): Finding[] {
  const rules = opts.rules === undefined ? RULES : RULES.filter((r) => opts.rules!.includes(r.id));
  const findings: Finding[] = [];
  for (const rule of rules) {
    for (const finding of rule.check(data)) {
      const reason = suppressionReason(data, finding);
      if (reason === undefined) findings.push(finding);
      else if (opts.includeSuppressed === true) findings.push({ ...finding, suppressed: reason });
    }
  }
  return findings.sort(compareFindings);
}
